import { useState, useEffect, useMemo, useRef } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  ExternalLink,
  CalendarIcon,
  Check,
  ChevronDown,
  FileText,
  AlertCircle,
  CheckCircle2,
  Trash2,
  RotateCcw,
  FileSearch,
  Loader2,
  FolderOpen,
  FolderPlus,
  RefreshCw,
  AlertTriangle,
  XCircle,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import {
  useDocument,
  useDocumentPreview,
  useDocumentProcessLogs,
  useRouteDocument,
  useDeleteDocument,
  useRetryDocument,
  useParseDocument,
  useRemoveMissingDocuments,
  isDropboxNotFoundError,
  isDocumentType,
  documentUsesLotId,
  buildRoutedFilename,
  DocumentType,
  DOC_TYPE_FOLDERS,
} from "@/hooks/useDocuments";
import { useClients, useClientFacilityFolders } from "@/hooks/useClients";
import { useDocumentFacilities } from "@/hooks/useFacilities";
import { usePicklistValues } from "@/hooks/usePicklistValues";
import { useWoActions } from "@/hooks/useWoActions";
import {
  useDropboxClientFolders,
  useValidateDropboxFolder,
  useInvalidateDropboxCache,
} from "@/hooks/useDropboxFolders";
import { ClientSelect } from "@/components/shared/ClientSelect";
import { PDFViewer } from "./PDFViewer";
import { OcrProcessingCard } from "./OcrProcessingCard";
import { RawOcrTextSection } from "./RawOcrTextSection";
import { matchClientDropboxFolder } from "@/lib/document-folder-matching";

interface DocumentReviewSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentId: string | null;
}

// Indicates a parsed lot_id actually names multiple lots (e.g. "PD24CSTT, PD24CSTT2")
const MULTIPLE_LOTS_PATTERN = /[,;&\/]/;

// Document-type-specific actions.
// WO is intentionally absent here -- it's sourced live from the wo_action
// table (org-scoped, admin-managed via Settings > Docs > Glossary), which is
// kept in sync with reqs/27-glossary.md. See useWoActions() below; the value
// used for each option is the real glossary action name (e.g. "Rack &
// Return"), not an invented camelCase token.
const DOC_TYPE_ACTIONS: Record<Exclude<DocumentType, "WO">, { value: string; label: string }[]> = {
  BOL: [
    { value: "BulkWineIn", label: "Bulk Wine In" },
    { value: "BulkWineOut", label: "Bulk Wine Out" },
    { value: "GlassIn", label: "Glass In" },
    { value: "CorkIn", label: "Cork In" },
    { value: "LabelsIn", label: "Labels In" },
    { value: "BarrelsIn", label: "Barrels In" },
    { value: "BarrelsOut", label: "Barrels Out" },
    { value: "AdditivesIn", label: "Additives In" },
    { value: "YeastIn", label: "Yeast In" },
    { value: "BacteriaIn", label: "Bacteria In" },
    { value: "BottledWineIn", label: "Bottled Wine In" },
    { value: "BottledWineOut", label: "Bottled Wine Out" },
  ],
  // Only one type of weigh tag — no subtypes needed.
  WEIGH: [],
  BRIX: [
    { value: "Reds", label: "Reds" },
    { value: "Whites", label: "Whites" },
  ],
  // Single-purpose doc type, like Weigh Tag — no action dropdown needed.
  EXTRACTION: [],
};

// Retired action values that may still be on older parse results (or come
// from a parse-document deploy that predates the rename) -> current value.
// "Cased Goods In/Out" was relabeled "Bottled Wine In/Out" on 2026-07-23 but
// kept its old value until 2026-09-29, so routed filenames still said
// "Cased Goods".
const LEGACY_ACTION_VALUES: Record<string, string> = {
  CasedGoodsIn: "BottledWineIn",
  CasedGoodsOut: "BottledWineOut",
};

// Helper to get actions for a document type ("" = no type chosen yet)
const getActionsForDocType = (
  docType: DocumentType | "",
  woActionOptions: { value: string; label: string }[]
) => (!docType ? [] : docType === "WO" ? woActionOptions : DOC_TYPE_ACTIONS[docType]);

// Filename/route action for doc types with no action field in the UI
// (WEIGH and EXTRACTION have no subtypes to choose from — see DOC_TYPE_ACTIONS
// above — so there's nothing for `action` state to ever hold for them).
const NO_ACTION_DOC_TYPE_DEFAULTS: Partial<Record<DocumentType, string>> = {
  WEIGH: "WeighTag",
  EXTRACTION: "Extractions",
};

// Resolves the action actually used for filenames/routing: the user's
// selection (or custom text) when one exists, otherwise the doc type's fixed
// default for types with no action field.
const getEffectiveAction = (docType: DocumentType | "", action: string, customAction: string) =>
  (action === "custom" ? customAction : action) || (docType && NO_ACTION_DOC_TYPE_DEFAULTS[docType]) || "";

export function DocumentReviewSheet({
  open,
  onOpenChange,
  documentId,
}: DocumentReviewSheetProps) {
  const { data: document, isLoading } = useDocument(documentId || "");
  // Load preview for all documents - backend handles fetching from correct path (inbox or routed destination)
  const { data: previewUrl, isLoading: previewLoading, error: previewError } = useDocumentPreview(
    document ? documentId : null
  );
  // Fetch AI processing logs for visibility into OCR pipeline
  const { data: processLogs } = useDocumentProcessLogs(documentId);

  const routeDocument = useRouteDocument();
  const deleteDocument = useDeleteDocument();
  const retryDocument = useRetryDocument();
  const parseDocument = useParseDocument();
  const removeMissingDocument = useRemoveMissingDocuments();

  // Check if the preview error indicates the file no longer exists in Dropbox
  const isFileNotFound = previewError && isDropboxNotFoundError(previewError);
  const { data: clients } = useClients();
  const { data: docTypePicklist = [] } = usePicklistValues("doc_type");
  // Only offer types routing actually knows a Dropbox folder for, even if a
  // retired picklist row is still active somewhere.
  const docTypes = docTypePicklist.filter((t) => isDocumentType(t.value));
  const { data: facilities = [] } = useDocumentFacilities();
  const { invalidateClientFolders, invalidateFolderValidation } = useInvalidateDropboxCache();
  // WO actions are org-managed in the wo_action table (Settings > Docs >
  // Glossary), not hardcoded like the other doc types' actions.
  const { data: woActions = [] } = useWoActions();
  const woActionOptions = useMemo(
    () => woActions.map((a) => ({ value: a.name, label: a.name })),
    [woActions]
  );

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [folderPopoverOpen, setFolderPopoverOpen] = useState(false);

  // Form state
  const [date, setDate] = useState<Date | undefined>();
  const [facilityId, setFacilityId] = useState<string>("");
  const [clientId, setClientId] = useState<string>("");
  const [lotId, setLotId] = useState("");
  const [docType, setDocType] = useState<DocumentType | "">("");
  const [action, setAction] = useState("");
  const [customAction, setCustomAction] = useState("");
  const [dropboxFolder, setDropboxFolder] = useState<string>("");
  // Supply packing slips (BOL Yeast In, Cork In, ...) are filed by client
  // code, not lot -- no Lot ID field for those.
  const usesLotId = documentUsesLotId(docType, action);

  const selectedFacility = facilities.find((f) => f.id === facilityId);
  const { data: dropboxFolders, isLoading: foldersLoading } = useDropboxClientFolders(facilityId || undefined);
  const { data: clientFacilityFolders } = useClientFacilityFolders(clientId || undefined);

  // Whether the reviewer has manually changed Location for the document
  // currently open. Tracked separately from the rest of the form-reset effect
  // below so that a same-document refresh (e.g. clicking "Parse OCR" or
  // "Restart", which changes document.parse_result/status but not which
  // document this is) can't silently revert a misfile correction back to the
  // auto-detected facility.
  const facilityManuallySetRef = useRef(false);

  // Location defaults to whichever inbox the file was detected under —
  // human-overridable via the Location dropdown below. Re-applies the
  // auto-detected facility on every document data refresh EXCEPT when the
  // reviewer has manually overridden it for this document.
  useEffect(() => {
    if (!facilityManuallySetRef.current) {
      setFacilityId(document?.facility_id || "");
    }
  }, [document?.facility_id]);

  // New document opened -- clear manual-override tracking so auto-detection
  // applies again.
  useEffect(() => {
    facilityManuallySetRef.current = false;
  }, [document?.id]);

  // Reset the rest of the form when document changes
  useEffect(() => {
    if (document?.parse_result) {
      const pr = document.parse_result;
      // Parse date string as local time to avoid timezone shift
      // "2026-01-14" should become Jan 14, not Jan 13 (which happens with UTC parsing)
      if (pr.parsed_date) {
        const [year, month, day] = pr.parsed_date.split('-').map(Number);
        setDate(new Date(year, month - 1, day)); // month is 0-indexed
      } else {
        setDate(undefined);
      }
      setClientId(pr.parsed_client_id || "");
      // A parsed lot_id naming multiple lots (e.g. a BOL covering several lots)
      // isn't a usable single Lot ID — fall back to the client code so the
      // field starts from something correct rather than a garbled multi-lot
      // string the user would otherwise have to notice and clear themselves.
      // Brix & Temp sheets are filed by client, so Lot ID is the client code.
      setLotId(
        pr.doc_type === "BRIX" || (pr.parsed_lot_id && MULTIPLE_LOTS_PATTERN.test(pr.parsed_lot_id))
          ? pr.parsed_client_code || ""
          : pr.parsed_lot_id || ""
      );
      // Blank (reviewer must choose) when the parser couldn't tell, or when
      // an older parse used the retired "OTHER" type -- never a guess.
      setDocType(isDocumentType(pr.doc_type) ? pr.doc_type : "");
      setAction(pr.parsed_action ? LEGACY_ACTION_VALUES[pr.parsed_action] || pr.parsed_action : "");
      setCustomAction("");
      setDropboxFolder("");
    } else {
      setDate(undefined);
      setClientId("");
      setLotId("");
      setDocType("");
      setAction("");
      setCustomAction("");
      setDropboxFolder("");
    }
  }, [document]);

  // Keep a Brix sheet's Lot ID in step with the client code when the reviewer
  // picks a different client or switches the doc type to Brix.
  useEffect(() => {
    if (docType !== "BRIX") return;
    const code = clients?.find((c) => c.id === clientId)?.code;
    if (code) setLotId(code);
  }, [docType, clientId, clients]);

  // Auto-select Dropbox folder based on selected client + facility. Re-runs
  // when facility changes (switching Location re-filters the folder list and
  // re-derives the match) as well as when client changes.
  useEffect(() => {
    if (!selectedFacility || !dropboxFolders) return;

    // A folder from a previously-selected facility never belongs to the new
    // one -- clear it so the combobox doesn't keep showing a stale
    // cross-facility selection after switching Location.
    if (dropboxFolder && !dropboxFolders.some((f) => f.name === dropboxFolder)) {
      setDropboxFolder("");
    }

    if (!clientId || !selectedFacility.dropbox_folder_prefix || dropboxFolders.length === 0) return;

    const selectedClientForFolder = clients?.find(c => c.id === clientId);
    if (!selectedClientForFolder) return;

    const savedFolder = clientFacilityFolders?.find((f) => f.facility_id === facilityId)?.dropbox_folder ?? null;
    const matched = matchClientDropboxFolder(selectedClientForFolder, dropboxFolders, selectedFacility.dropbox_folder_prefix, savedFolder);
    if (matched) {
      setDropboxFolder(matched);
    }
    // dropboxFolder is intentionally read (for the stale-value check above)
    // but not a dependency: including it would re-run this effect on every
    // manual folder selection and immediately overwrite it with the
    // auto-match suggestion. dropboxFolders already changes whenever
    // facilityId does, which is what the stale-value check needs to react to.
  }, [clientId, facilityId, selectedFacility, clients, dropboxFolders, clientFacilityFolders]);

  const handleApproveAndRoute = () => {
    if (!documentId || !date || !facilityId || !clientId || (usesLotId && !lotId) || !docType || !dropboxFolder) return;

    const finalAction = getEffectiveAction(docType, action, customAction);

    // Close immediately rather than waiting on the Dropbox move so the
    // reviewer can move on to the next document. The queue row flips to
    // "Routing" right away, and useRouteDocument's toasts report the outcome
    // (a failure lands the document in Failed with the error attached).
    routeDocument.mutate({
      document_id: documentId,
      doc_type: docType,
      date: format(date, "yyyy-MM-dd"),
      client_id: clientId,
      lot_id: lotId,
      action: finalAction || "General",
      dropbox_folder: dropboxFolder,
      facility_id: facilityId,
    });
    onOpenChange(false);
  };

  const handleDelete = () => {
    if (!documentId) return;
    deleteDocument.mutate(documentId, {
      onSuccess: () => {
        setShowDeleteDialog(false);
        onOpenChange(false);
      },
    });
  };

  const handleRetry = () => {
    if (!documentId) return;
    retryDocument.mutate(documentId);
  };

  const handleParse = () => {
    if (!documentId) return;
    parseDocument.mutate(documentId);
  };

  // Get selected client info for path generation
  const selectedClient = clients?.find(c => c.id === clientId);

  // Generate preview filename (Dropbox convention: YYYY.MM.DD LotID Action.pdf or YYYY.MM.DD ClientCode Action.pdf)
  // If lot ID exists (single lot), use it since lot codes already include client code prefix
  // If multiple lots or no lot, use just the client code
  const generatePreviewFilename = () => {
    if (!date) return usesLotId ? "YYYY.MM.DD LotID Action.pdf" : "YYYY.MM.DD ClientCode Action.pdf";
    return buildRoutedFilename({
      date,
      lotId,
      clientCode: selectedClient?.code || selectedClient?.name?.substring(0, 2).toUpperCase() || "XX",
      action: getEffectiveAction(docType, action, customAction) || "Action",
      usesLotId,
    });
  };

  // Generate the full expected destination path
  const generateExpectedPath = () => {
    const year = date?.getFullYear() || new Date().getFullYear();
    const filename = generatePreviewFilename();

    return {
      folder: dropboxFolder && docType ? `/GWH Clients/${dropboxFolder}/${DOC_TYPE_FOLDERS[docType]}/${year}/` : null,
      filename,
    };
  };

  const expectedPath = generateExpectedPath();

  // Validate the destination folder exists in Dropbox
  const destinationFolderPath = expectedPath.folder?.slice(0, -1) || null; // Remove trailing slash
  const {
    data: folderValidation,
    isLoading: validationLoading,
    refetch: refetchValidation,
  } = useValidateDropboxFolder(destinationFolderPath, { quick: true });
  // The client folder always exists (it's picked from existing Dropbox
  // folders), so a not-found destination means only the doc-type/year
  // subfolder is missing -- route-document creates it on Approve & Route.
  // Anything else (permissions, Dropbox errors) is a real problem.
  const folderWillBeCreated = !!folderValidation && !folderValidation.valid && folderValidation.errorCode === 'path_not_found';
  const folderValidationFailed = !!folderValidation && !folderValidation.valid && !folderWillBeCreated;

  const requiresAction = getActionsForDocType(docType, woActionOptions).length > 0;
  const isFormValid = date && facilityId && clientId && (!usesLotId || lotId) && docType && (!requiresAction || action || customAction) && dropboxFolder;

  const statusConfig: Record<string, { label: string; color: string }> = {
    detected: { label: "Detected", color: "bg-slate-500" },
    parsing: { label: "Parsing", color: "bg-blue-500" },
    parsed: { label: "Parsed", color: "bg-purple-500" },
    needs_review: { label: "Needs Review", color: "bg-amber-500" },
    routing: { label: "Routing", color: "bg-indigo-500" },
    routed: { label: "Routed", color: "bg-green-500" },
    failed: { label: "Failed", color: "bg-red-500" },
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-40 w-full" />
            </div>
          ) : !document ? (
            <div className="flex flex-col items-center justify-center h-full">
              <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Document not found</p>
            </div>
          ) : (
            <>
              <SheetHeader>
                <div className="flex items-center gap-2">
                  <SheetTitle className="text-lg truncate flex-1">
                    {document.original_filename}
                  </SheetTitle>
                  <Badge className={statusConfig[document.status]?.color || "bg-slate-500"}>
                    {statusConfig[document.status]?.label || document.status}
                  </Badge>
                </div>
                <SheetDescription>
                  Detected {format(new Date(document.detected_at), "PPp")}
                </SheetDescription>
              </SheetHeader>

              <div className="space-y-6 mt-6">
                {/* PDF Preview */}
                {previewLoading ? (
                  <div className="h-64 bg-muted rounded-lg flex items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : previewUrl ? (
                  <div className="space-y-2">
                    <PDFViewer url={previewUrl} />
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="flex-1" asChild>
                        <a href={previewUrl} download={document.original_filename}>
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Download PDF
                        </a>
                      </Button>
                      {document.status !== 'routed' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={handleParse}
                          disabled={parseDocument.isPending || ['parsed', 'needs_review', 'routing'].includes(document.status)}
                        >
                          {parseDocument.isPending ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Parsing...
                            </>
                          ) : (
                            <>
                              <FileSearch className="h-4 w-4 mr-2" />
                              Parse OCR
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                ) : document.route?.dropbox_share_url ? (
                  <Button variant="outline" className="w-full" asChild>
                    <a
                      href={document.route.dropbox_share_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Open in Dropbox
                    </a>
                  </Button>
                ) : isFileNotFound ? (
                  <div className="p-4 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg text-center space-y-3">
                    <XCircle className="h-10 w-10 mx-auto text-red-500" />
                    <div>
                      <p className="font-medium text-red-700 dark:text-red-300">File Not Found in Dropbox</p>
                      <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                        This file may have been renamed, moved, or deleted.
                      </p>
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        if (documentId) {
                          removeMissingDocument.mutate(documentId, {
                            onSuccess: () => {
                              onOpenChange(false);
                            },
                          });
                        }
                      }}
                      disabled={removeMissingDocument.isPending}
                    >
                      {removeMissingDocument.isPending ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Removing...
                        </>
                      ) : (
                        <>
                          <Trash2 className="h-4 w-4 mr-2" />
                          Remove from List
                        </>
                      )}
                    </Button>
                  </div>
                ) : previewError ? (
                  <div className="p-4 bg-muted rounded-lg text-center text-sm text-muted-foreground">
                    <AlertCircle className="h-8 w-8 mx-auto mb-2 text-red-500" />
                    <span className="text-red-500">Error loading preview</span>
                    <p className="text-xs mt-1 text-muted-foreground">{(previewError as Error).message}</p>
                  </div>
                ) : document.status === 'routed' && document.route?.final_dropbox_path ? (
                  <div className="p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg text-center space-y-3">
                    <AlertTriangle className="h-10 w-10 mx-auto text-amber-500" />
                    <div>
                      <p className="font-medium text-amber-700 dark:text-amber-300">Preview Unavailable</p>
                      <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                        File may have been renamed in Dropbox after routing.
                      </p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="mx-auto"
                        onClick={() => {
                          // Open Dropbox folder (parent of the file path)
                          const folderPath = document.route?.final_dropbox_path?.split('/').slice(0, -1).join('/');
                          if (folderPath) {
                            window.open(`https://www.dropbox.com/home${folderPath}`, '_blank');
                          }
                        }}
                      >
                        <FolderOpen className="h-4 w-4 mr-2" />
                        Open Folder in Dropbox
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mx-auto text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => {
                          if (documentId) {
                            removeMissingDocument.mutate(documentId, {
                              onSuccess: () => {
                                onOpenChange(false);
                              },
                            });
                          }
                        }}
                        disabled={removeMissingDocument.isPending}
                      >
                        {removeMissingDocument.isPending ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Removing...
                          </>
                        ) : (
                          <>
                            <Trash2 className="h-4 w-4 mr-2" />
                            Remove from List
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 bg-muted rounded-lg text-center text-sm text-muted-foreground">
                    <FileText className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
                    Unable to load preview
                  </div>
                )}

                {/* Stuck in Parsing - Restart Button */}
                {document.status === 'parsing' && (
                  <div className="p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span className="text-sm font-medium">Parsing in progress...</span>
                        </div>
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                          Typically takes 15-45 seconds. If longer than 2 minutes, restart.
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                        onClick={handleRetry}
                        disabled={retryDocument.isPending}
                      >
                        {retryDocument.isPending ? (
                          <>
                            <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                            Restarting...
                          </>
                        ) : (
                          <>
                            <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                            Restart
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {/* AI OCR Section */}
                {document.parse_result && (
                  <OcrProcessingCard
                    documentId={document.id}
                    status={document.status}
                    parseResult={document.parse_result}
                    processLogs={processLogs}
                  />
                )}

                {/* Error Message - only show for non-routed documents */}
                {document.error_message && document.status !== 'routed' && (
                  <div className="p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <p className="text-sm text-red-600 dark:text-red-400">
                      {document.error_message}
                    </p>
                  </div>
                )}

                <Separator />

                {/* Show different content based on document status */}
                {document.status === 'routed' ? (
                  /* ============================================================
                     ROUTED DOCUMENT - Static Summary View
                     ============================================================ */
                  <>
                    <div className="space-y-4">
                      <h3 className="font-medium flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        Document Routed Successfully
                      </h3>

                      {/* Routed timestamp */}
                      {document.route?.approved_at && (
                        <div className="text-sm text-muted-foreground">
                          Routed on {format(new Date(document.route.approved_at), "PPP 'at' p")}
                        </div>
                      )}

                      {/* Summary card */}
                      <div className="p-4 bg-muted/50 rounded-lg space-y-3">
                        {/* Client */}
                        {(document.client || document.parse_result?.parsed_client_name) && (
                          <div className="flex justify-between items-start">
                            <span className="text-sm text-muted-foreground">Client</span>
                            <span className="text-sm font-medium text-right">
                              {document.client?.name || document.parse_result?.parsed_client_name}
                              {document.client?.code && (
                                <span className="text-muted-foreground ml-1">({document.client.code})</span>
                              )}
                            </span>
                          </div>
                        )}

                        {/* Document Type */}
                        {document.parse_result?.doc_type && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-muted-foreground">Document Type</span>
                            <span className="text-sm font-medium">
                              {docTypes.find(t => t.value === document.parse_result?.doc_type)?.label || document.parse_result.doc_type}
                            </span>
                          </div>
                        )}

                        {/* Date */}
                        {document.parse_result?.parsed_date && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-muted-foreground">Document Date</span>
                            <span className="text-sm font-medium">
                              {(() => {
                                // Parse as local time to avoid timezone shift
                                const [y, m, d] = document.parse_result.parsed_date.split('-').map(Number);
                                return format(new Date(y, m - 1, d), "PPP");
                              })()}
                            </span>
                          </div>
                        )}

                        {/* Lot ID */}
                        {document.parse_result?.parsed_lot_id && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-muted-foreground">Lot ID</span>
                            <span className="text-sm font-medium font-mono">
                              {document.parse_result.parsed_lot_id}
                            </span>
                          </div>
                        )}

                        {/* Action */}
                        {document.parse_result?.parsed_action && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-muted-foreground">Action</span>
                            <span className="text-sm font-medium">
                              {document.parse_result.parsed_action}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Final destination */}
                      {document.route?.final_dropbox_path && (
                        <div className="space-y-2">
                          <Label className="text-muted-foreground flex items-center gap-1.5">
                            <FolderOpen className="h-3.5 w-3.5" />
                            Final Destination
                          </Label>
                          <div className="p-3 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg">
                            <div className="text-xs font-mono break-all text-green-700 dark:text-green-300">
                              {document.route.final_dropbox_path}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* OCR Text (collapsible) */}
                    <RawOcrTextSection text={document.parse_result?.raw_ocr_text} />

                    <Separator />

                    {/* Actions for routed documents */}
                    <div className="space-y-3">
                      {document.route?.dropbox_share_url && (
                        <Button className="w-full" variant="outline" asChild>
                          <a
                            href={document.route.dropbox_share_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <ExternalLink className="h-4 w-4 mr-2" />
                            Open in Dropbox
                          </a>
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => onOpenChange(false)}
                      >
                        Close
                      </Button>
                    </div>
                  </>
                ) : (
                  /* ============================================================
                     NON-ROUTED DOCUMENT - Editable Form View
                     ============================================================ */
                  <>
                    {/* Metadata Form */}
                    <div className="space-y-4">
                      <h3 className="font-medium">Document Metadata</h3>

                      {/* Date */}
                      <div className="space-y-2">
                        <Label>Document Date *</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal",
                                !date && "text-muted-foreground"
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {date ? format(date, "PPP") : "Pick a date"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={date}
                              onSelect={setDate}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>

                      {/* Location — auto-set from the inbox this scan was detected in;
                          override here if it was misfiled into the wrong facility's inbox. */}
                      <div className="space-y-2">
                        <Label>Location *</Label>
                        <Select
                          value={facilityId}
                          onValueChange={(v) => {
                            facilityManuallySetRef.current = true;
                            setFacilityId(v);
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select location..." />
                          </SelectTrigger>
                          <SelectContent>
                            {facilities.map((facility) => (
                              <SelectItem key={facility.id} value={facility.id}>
                                {facility.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Client */}
                      <div className="space-y-2">
                        <Label>Client *</Label>
                        <ClientSelect
                          value={clientId}
                          onValueChange={setClientId}
                          placeholder="Select client..."
                        />
                      </div>

                      {/* Dropbox Folder - Searchable Combobox */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label>Dropbox Folder *</Label>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs"
                            onClick={() => invalidateClientFolders()}
                            disabled={foldersLoading}
                          >
                            <RefreshCw className={cn("h-3 w-3 mr-1", foldersLoading && "animate-spin")} />
                            Refresh
                          </Button>
                        </div>
                        <Popover open={folderPopoverOpen} onOpenChange={setFolderPopoverOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-expanded={folderPopoverOpen}
                              className="w-full justify-between font-normal"
                            >
                              {dropboxFolder
                                ? dropboxFolders?.find((f) => f.name === dropboxFolder)?.name
                                : foldersLoading
                                ? "Loading folders..."
                                : "Select destination folder..."}
                              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                            <Command>
                              <CommandInput placeholder="Search folders..." />
                              <CommandList
                                className="max-h-60"
                                onWheelCapture={(e) => e.stopPropagation()}
                              >
                                <CommandEmpty>No folder found.</CommandEmpty>
                                <CommandGroup>
                                  {dropboxFolders?.map((folder) => (
                                    <CommandItem
                                      key={folder.path}
                                      value={folder.name}
                                      onSelect={() => {
                                        setDropboxFolder(folder.name === dropboxFolder ? "" : folder.name);
                                        setFolderPopoverOpen(false);
                                      }}
                                    >
                                      <Check
                                        className={cn(
                                          "mr-2 h-4 w-4",
                                          dropboxFolder === folder.name ? "opacity-100" : "opacity-0"
                                        )}
                                      />
                                      {folder.name}
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                        <p className="text-xs text-muted-foreground">
                          Type to search or select the client's folder
                        </p>
                      </div>

                      {/* Lot ID — hidden for supply packing slips, which file by client code */}
                      {usesLotId && (
                        <div className="space-y-2">
                          <Label>Lot ID *</Label>
                          <Input
                            value={lotId}
                            onChange={(e) => setLotId(e.target.value)}
                            placeholder="e.g., 25PNGV1A"
                          />
                          <p className="text-xs text-muted-foreground">
                            Format: [Vintage][Variety][Block/ID] (e.g., 25PNGV1A)
                          </p>
                        </div>
                      )}

                      {/* Document Type */}
                      <div className="space-y-2">
                        <Label>Document Type *</Label>
                        <Select
                          value={docType}
                          onValueChange={(v) => {
                            const newType = v as DocumentType;
                            setDocType(newType);
                            // Reset action if current action is not valid for new type
                            const validActions = getActionsForDocType(newType, woActionOptions).map(a => a.value);
                            if (action && action !== "custom" && !validActions.includes(action)) {
                              setAction("");
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select document type..." />
                          </SelectTrigger>
                          <SelectContent>
                            {docTypes.map((type) => (
                              <SelectItem key={type.value} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Action — hidden for doc types with no subtypes (e.g. Weigh Tag, Daily Extractions) */}
                      {getActionsForDocType(docType, woActionOptions).length > 0 && (
                        <div className="space-y-2">
                          <Label>Action</Label>
                          <Select value={action} onValueChange={setAction}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select action..." />
                            </SelectTrigger>
                            <SelectContent>
                              {getActionsForDocType(docType, woActionOptions).map((a) => (
                                <SelectItem key={a.value} value={a.value}>
                                  {a.label}
                                </SelectItem>
                              ))}
                              <SelectItem value="custom">Custom...</SelectItem>
                            </SelectContent>
                          </Select>
                          {action === "custom" && (
                            <Input
                              value={customAction}
                              onChange={(e) => setCustomAction(e.target.value)}
                              placeholder="Enter custom action..."
                              className="mt-2"
                            />
                          )}
                        </div>
                      )}
                    </div>

                    <Separator />

                    {/* Expected Destination Preview with Validation */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-muted-foreground flex items-center gap-1.5">
                          <FolderOpen className="h-3.5 w-3.5" />
                          Expected Destination
                        </Label>
                        {expectedPath.folder && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs"
                            onClick={() => {
                              invalidateFolderValidation(destinationFolderPath || undefined);
                              refetchValidation();
                            }}
                            disabled={validationLoading}
                          >
                            <RefreshCw className={cn("h-3 w-3 mr-1", validationLoading && "animate-spin")} />
                            Verify
                          </Button>
                        )}
                      </div>
                      {expectedPath.folder ? (
                        <div
                          className={cn(
                            "p-3 rounded-lg space-y-2 border",
                            folderValidation?.valid
                              ? "bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800"
                              : folderWillBeCreated
                              ? "bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800"
                              : folderValidationFailed
                              ? "bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800"
                              : "bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800"
                          )}
                        >
                          <div className="text-xs font-mono break-all text-muted-foreground">
                            {expectedPath.folder}
                          </div>
                          <div
                            className={cn(
                              "font-mono text-sm font-medium break-all",
                              folderValidation?.valid
                                ? "text-green-700 dark:text-green-300"
                                : folderWillBeCreated
                                ? "text-amber-700 dark:text-amber-300"
                                : folderValidationFailed
                                ? "text-red-700 dark:text-red-300"
                                : "text-blue-700 dark:text-blue-300"
                            )}
                          >
                            {expectedPath.filename}
                          </div>

                          {/* Validation Status */}
                          <div className="flex items-center gap-2 pt-1 border-t border-current/10">
                            {validationLoading ? (
                              <>
                                <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                                <span className="text-xs text-muted-foreground">Validating folder...</span>
                              </>
                            ) : folderValidation?.valid ? (
                              <>
                                <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                                <span className="text-xs text-green-600 dark:text-green-400">
                                  Folder verified
                                </span>
                              </>
                            ) : folderWillBeCreated ? (
                              <>
                                <FolderPlus className="h-3.5 w-3.5 text-amber-600 flex-shrink-0" />
                                <span className="text-xs text-amber-600 dark:text-amber-400">
                                  New folder — will be created when routed
                                </span>
                              </>
                            ) : folderValidationFailed ? (
                              <div className="space-y-1 w-full">
                                <div className="flex items-center gap-2">
                                  <XCircle className="h-3.5 w-3.5 text-red-600 flex-shrink-0" />
                                  <span className="text-xs text-red-600 dark:text-red-400">
                                    {folderValidation.error || "Folder not found"}
                                  </span>
                                </div>
                                {folderValidation.diagnostics?.freshScan?.availableFolders && (
                                  <div className="text-xs text-muted-foreground pl-5">
                                    Available folders: {folderValidation.diagnostics.freshScan.availableFolders.slice(0, 5).join(", ")}
                                    {folderValidation.diagnostics.freshScan.foldersFound > 5 && ` (+${folderValidation.diagnostics.freshScan.foldersFound - 5} more)`}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <>
                                <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                                <span className="text-xs text-muted-foreground">
                                  Click "Verify" to check folder exists
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="p-3 bg-muted/50 border border-dashed rounded-lg">
                          <p className="text-sm text-muted-foreground">
                            Select a Dropbox folder to see the destination path
                          </p>
                        </div>
                      )}
                    </div>

                    {/* OCR Text */}
                    <RawOcrTextSection text={document.parse_result?.raw_ocr_text} />

                    <Separator />

                    {/* Actions */}
                    <div className="space-y-3">
                      <Button
                        className="w-full"
                        onClick={handleApproveAndRoute}
                        disabled={!isFormValid || routeDocument.isPending}
                      >
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        {routeDocument.isPending ? "Routing..." : "Approve & Route"}
                      </Button>

                      <div className="flex gap-2">
                        {document.status === "failed" && (
                          <Button
                            variant="outline"
                            className="flex-1"
                            onClick={handleRetry}
                            disabled={retryDocument.isPending}
                          >
                            <RotateCcw className="h-4 w-4 mr-2" />
                            Retry
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          className="flex-1"
                          onClick={() => onOpenChange(false)}
                        >
                          Skip
                        </Button>
                        <Button
                          variant="destructive"
                          size="icon"
                          onClick={() => setShowDeleteDialog(true)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this document from the processing queue?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
